// Right-rail panel: every attachment across the whole ticket thread in one
// place, so you don't have to scroll the conversation to find a file. This is
// ADDITIVE — the per-message attachment rendering (MessageBody) is untouched.
//
// Aggregates ticket_messages.attachments (jsonb, shape mirrors what
// 01-pipeline.js / 12-send-reply.js store: { filename, contentType, size_bytes,
// url, content_id }), de-dupes by URL, tags each with its message date, skips
// inline-embedded images (cid signature logos), groups by type into tabs, and
// sorts newest-first so the latest files are at the top.

import { useMemo } from 'react';
import { Paperclip, Image as ImageIcon, FileText, FileSpreadsheet, FileArchive, File, type LucideIcon } from 'lucide-react';
import { useTicketMessages } from '@/lib/queries';
import { AttachmentTile, type AttachmentLike } from '@/components/AttachmentTile';
import { RelativeTime } from '@/components/RelativeTime';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CappedBody, RailSection } from '@/components/RailSection';

interface RawAttachment {
  url?: string;
  filename?: string;
  contentType?: string;
  size_bytes?: number;
  size?: number;
  content_id?: string | null;
}

// An attachment plus the date of the message it came in on.
interface Entry {
  att: AttachmentLike;
  date: string | null;
}

type Category = 'Images' | 'Documents' | 'Spreadsheets' | 'Archives' | 'Other';

const CATEGORY_ORDER: Category[] = ['Images', 'Documents', 'Spreadsheets', 'Archives', 'Other'];
const CATEGORY_ICON: Record<Category, LucideIcon> = {
  Images: ImageIcon,
  Documents: FileText,
  Spreadsheets: FileSpreadsheet,
  Archives: FileArchive,
  Other: File,
};

function categorize(att: AttachmentLike): Category {
  const ct = (att.contentType || '').toLowerCase();
  const name = (att.filename || '').toLowerCase();
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
  if (ct.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'heic', 'tiff'].includes(ext)) return 'Images';
  if (ct.includes('spreadsheet') || ct.includes('excel') || ct.includes('csv') || ['xls', 'xlsx', 'csv', 'ods', 'numbers'].includes(ext)) return 'Spreadsheets';
  if (ct.includes('zip') || ct.includes('compressed') || ct.includes('x-tar') || ct.includes('x-7z') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'Archives';
  if (
    ct.includes('pdf') || ct.includes('word') || ct.includes('msword') ||
    ct.includes('officedocument.wordprocessing') || ct.includes('opendocument.text') ||
    ct.startsWith('text/') || ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt', 'pages', 'md'].includes(ext)
  ) return 'Documents';
  return 'Other';
}

// Newest first; nulls last.
function byDateDesc(a: Entry, b: Entry): number {
  const ta = a.date ? Date.parse(a.date) : 0;
  const tb = b.date ? Date.parse(b.date) : 0;
  return tb - ta;
}

export function ThreadAttachmentsPanel({ ticketId }: { ticketId: string }) {
  const messagesQ = useTicketMessages(ticketId);

  const { total, groups, allList } = useMemo(() => {
    const seen = new Set<string>();
    const byCat: Record<Category, Entry[]> = { Images: [], Documents: [], Spreadsheets: [], Archives: [], Other: [] };
    let count = 0;
    for (const m of messagesQ.data ?? []) {
      const date = (m as { created_at?: string }).created_at ?? null;
      const list = (Array.isArray((m as { attachments?: unknown }).attachments)
        ? (m as { attachments: RawAttachment[] }).attachments
        : []) as RawAttachment[];
      for (const a of list) {
        if (!a || !a.url) continue;
        const isImage = typeof a.contentType === 'string' && a.contentType.startsWith('image/');
        // Inline images referenced by cid (signature logos, embedded marks)
        // are part of the message body, not standalone attachments — skip.
        if (a.content_id && isImage) continue;
        if (seen.has(a.url)) continue;
        seen.add(a.url);
        const entry: Entry = {
          date,
          att: {
            url: a.url,
            filename: a.filename || 'file',
            contentType: a.contentType || '',
            size: typeof a.size_bytes === 'number' ? a.size_bytes : typeof a.size === 'number' ? a.size : 0,
          },
        };
        byCat[categorize(entry.att)].push(entry);
        count++;
      }
    }
    for (const cat of CATEGORY_ORDER) byCat[cat].sort(byDateDesc);
    const all = CATEGORY_ORDER.flatMap((c) => byCat[c]).slice().sort(byDateDesc);
    return { total: count, groups: byCat, allList: all };
  }, [messagesQ.data]);

  if (total === 0) return null;

  const presentCats = CATEGORY_ORDER.filter((cat) => groups[cat].length > 0);

  const renderList = (entries: Entry[]) => (
    <div className="space-y-2.5">
      {entries.map((e) => (
        <div key={e.att.url}>
          <AttachmentTile att={e.att} />
          {e.date && <RelativeTime value={e.date} className="mt-0.5 block text-3xs text-muted-foreground" />}
        </div>
      ))}
    </div>
  );

  // The cap lives inside each tab panel (CappedBody) rather than around
  // the whole <Tabs>, so the tab strip stays put while a long list is
  // collapsed behind "Show more".
  const body = (entries: Entry[]) => (
    <CappedBody maxHeightRem={16}>{renderList(entries)}</CappedBody>
  );

  return (
    <RailSection
      title="Attachments"
      icon={Paperclip}
      count={total}
      pre={
        <Tabs defaultValue="all">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
            <TabsTrigger
              value="all"
              className="h-7 rounded-md border border-transparent px-2 text-xs data-[state=active]:border-border data-[state=active]:bg-muted"
            >
              All {total}
            </TabsTrigger>
            {presentCats.map((cat) => {
              const Icon = CATEGORY_ICON[cat];
              return (
                <TabsTrigger
                  key={cat}
                  value={cat}
                  title={cat}
                  className="h-7 rounded-md border border-transparent px-2 text-xs data-[state=active]:border-border data-[state=active]:bg-muted"
                >
                  <Icon className="mr-1 h-3.5 w-3.5" /> {groups[cat].length}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="all" className="mt-2">
            {body(allList)}
          </TabsContent>
          {presentCats.map((cat) => (
            <TabsContent key={cat} value={cat} className="mt-2">
              {body(groups[cat])}
            </TabsContent>
          ))}
        </Tabs>
      }
    />
  );
}
